/**
 * OverlayErrorBoundary
 * ────────────────────
 * A React error boundary designed specifically for overlay components.
 *
 * When an overlay (ProjectDetail, CreateProject, ChallengeDetail, etc.)
 * throws during render, this catches it and shows a graceful error card
 * instead of crashing the entire Dashboard / cosmic map.
 *
 * Features:
 *   - Styled to match the overlay aesthetic (dark glass, centered)
 *   - "Dismiss" closes the overlay via the onClose callback
 *   - "Try Again" resets the boundary and re-renders the child
 *   - Logs the error for debugging
 *
 * Usage:
 *   <OverlayErrorBoundary onClose={overlays.closeProjectDetail} name="Project Details">
 *     <ProjectDetailOverlay ... />
 *   </OverlayErrorBoundary>
 *
 * Place at: src/components/overlays/OverlayErrorBoundary.jsx
 */

import React from 'react';
import { AlertTriangle, X, RotateCcw } from 'lucide-react';

class OverlayErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || 'Overlay';
    console.error(`[${name}] Render error caught by OverlayErrorBoundary:`, error);
    console.error('Component stack:', errorInfo?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleDismiss = () => {
    this.setState({ hasError: false, error: null });
    this.props.onClose?.();
  };

  // Reset when the overlay is toggled off and back on
  componentDidUpdate(prevProps) {
    if (this.state.hasError && this.props.resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      const name = this.props.name || 'overlay';
      const errorMessage = this.state.error?.message || 'An unexpected error occurred';

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={this.handleDismiss}
          />

          {/* Error card */}
          <div className="relative w-full max-w-sm bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Top accent bar */}
            <div className="h-1 w-full bg-gradient-to-r from-red-500 via-orange-500 to-amber-500" />

            <div className="p-6">
              {/* Icon + close */}
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <button
                  onClick={this.handleDismiss}
                  className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Message */}
              <h3 className="text-sm font-semibold text-white mb-1.5">
                Something went wrong
              </h3>
              <p className="text-[13px] text-zinc-400 leading-relaxed mb-1">
                The {name} couldn't load properly. This is usually temporary.
              </p>

              {/* Error detail (collapsed, subtle) */}
              <details className="mb-5 group">
                <summary className="text-[11px] text-zinc-600 cursor-pointer hover:text-zinc-500 transition-colors select-none">
                  Technical details
                </summary>
                <pre className="mt-2 text-[11px] text-red-400/70 bg-red-500/5 border border-red-500/10 rounded-lg p-3 overflow-x-auto max-h-24 font-mono leading-relaxed">
                  {errorMessage}
                </pre>
              </details>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-sm font-medium rounded-xl transition-all duration-200 active:scale-[0.98] shadow-lg shadow-purple-500/25"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Try Again
                </button>
                <button
                  onClick={this.handleDismiss}
                  className="px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium rounded-xl border border-zinc-700 transition-all duration-200 active:scale-[0.98]"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default OverlayErrorBoundary;